/**
 * Ground War simulation state (ARCHITECTURE 3.1). Pure types plus a few plain `const` objects.
 * The whole GwState is plain JSON: a full snapshot is JSON.stringify(state).
 *
 * Units: tick = 1/30 s, cx = 1/100 px, permille = 0..1000. All integers except GwProjectile.dmg.
 */

export const GW_TICK_HZ = 30;
export const GW_TICK_MS = 1000 / GW_TICK_HZ;
/** cx per world px. */
export const GW_FP = 100;
/** Hard cap incl. diggers; the pop cap normally binds first. */
export const GW_MAX_UNITS_PER_SIDE = 120;
export const GW_MAX_PROJECTILES = 256;
export const GW_ROWS = 3;

export type GwSide = 0 | 1;
export type GwStance = 'attack' | 'hold' | 'burrow';
export const GW_STANCES: readonly GwStance[] = ['attack', 'hold', 'burrow'];

/** Unit state machine (stored as a small int). */
export const GwUS = { Idle: 0, Walk: 1, Windup: 2, Recover: 3, Cast: 4, Mine: 5, Haul: 6, Garrisoned: 7, Dying: 8 } as const;
export type GwUnitStateId = (typeof GwUS)[keyof typeof GwUS];

/** Special target codes for GwUnit.tgt (>= 1 is a unit id). Turrets are never targeted, so they have no code. */
export const GwTgt = { None: 0, Base: -1, Wall: -2 } as const;

/** GwUnit.flags bit masks. */
export const GwUF = { Free: 1, Boss: 2, Summoned: 4, HuntDiggers: 8, HuntCart: 16, Cart: 32, Boosted: 64 } as const;

export interface GwUnit {
  /** Unique, ascending in spawn order; iteration order = id order. */
  id: number;
  /** Index into rules.units. */
  t: number;
  side: GwSide;
  /** cx */
  x: number;
  /** Depth row. */
  row: 0 | 1 | 2;
  /** Facing (+1 = towards the enemy for side 0). */
  face: 1 | -1;
  hp: number;
  /** Fixed at spawn from spec hp × kit × vitality × tree × unitHpPm (6.3); recomputed when an HP kit completes. */
  maxHp: number;
  /** State; Cast also holds every timed ability (kind from rules.units[t].ability.params.kind). */
  st: GwUnitStateId;
  /** Tick the current state began (animation clock; Cast: ability start). */
  stT: number;
  /** GwTgt code or unit id. */
  tgt: number;
  /** Tick the pending hit lands (Windup), the Cast end or next sub-hit, or 0. */
  hitAt: number;
  /** Tick the next attack may start. */
  nextAtk: number;
  /** Tick the ability is ready. */
  abReady: number;
  /** Last stun end, never reset; a new stun is refused inside the grace window after it. */
  stunTo: number;
  /** Slowed until tick. */
  slowTo: number;
  /** Slow amount permille (400 = 40 % slower). */
  slowPm: number;
  /** Knockback velocity, cx/tick, decays by rules.knockbackDecay per tick. */
  kbV: number;
  /** Digger: ore in the sack. */
  carry: number;
  /** Digger: seam of the current trip, 0 = none. */
  seam: number;
  /** Digger: seam pinned by assignDiggers, 0 = auto. */
  pin: number;
  /** Player id controlling it as champion, 'ai:<side>' for an AI champion, '' = none. */
  champ: string;
  /** champMoveTo target x in cx, -1 = none. */
  goal: number;
  /** Last tick it dealt or took a hit (blocked hits included); the spawn tick at spawn. */
  lastHit: number;
  /** Integer heal accumulator of the step-10b regeneration: heal = floor(regen / 30000). */
  regen: number;
  /** GwUF bits. */
  flags: number;
}

export interface GwProjectile {
  id: number;
  /** Index into rules.projectiles. */
  kind: number;
  side: GwSide;
  /** cx; y = height above ground (0 = ground). */
  x: number;
  y: number;
  /** cx/tick; gravity rules.projectiles[kind].gravityCx cx/tick². */
  vx: number;
  vy: number;
  /** Row it can hit. */
  row: 0 | 1 | 2;
  /** Attacker-side damage product, an unrounded double fixed at launch (the one non-integer in the state). */
  dmg: number;
  /** 1 = no block roll. */
  bypass: 0 | 1;
  /** Attacker role index (counter wheel resolved at impact). */
  role: number;
  /** 1 or 2. */
  siege: number;
  /** cx, 0 = single target. */
  aoeR: number;
  aoeMax: number;
  /** Attacker unit id (0 = turret/special/base slinger). */
  src: number;
  /** Fired by a Champion. */
  champ: 0 | 1;
  /** Tick it expires. */
  ttl: number;
  /** Targets it may still pass through. */
  pierce: number;
}

export interface GwSeam {
  id: number;
  /** cx */
  x: number;
  /** Remaining ore. */
  ore: number;
  max: number;
  /** Ore-per-swing multiplier permille (1000 home, 1500 centre). */
  richPm: number;
  /** 2 = centre seam. */
  home: GwSide | 2;
  /** How many times this seam slot has respawned. */
  gen: number;
  /** Centre seams only: side holding the claim this tick, -1 = nobody. */
  claim: -1 | GwSide;
}

export interface GwEffect {
  id: number;
  /** Index into rules.effects. */
  kind: number;
  /** Owner (damages the other side); 2 = neutral, damages both. */
  side: GwSide | 2;
  /** cx strip. */
  x0: number;
  x1: number;
  until: number;
  nextAt: number;
  period: number;
  /** Per pulse. */
  dmg: number;
  slowPm: number;
  /** Moving effects: cx/tick; 0 = static. */
  vx: number;
  /** Moving effects: unit ids already hit. */
  hitIds: number[];
}

export interface GwBase {
  hp: number;
  maxHp: number;
  /** cx, burrow door. */
  x: number;
  /** 1 = not targetable, takes no damage. */
  sealed: 0 | 1;
  /** Seal stop line in cx; 0 = no stop line. */
  stopCx: number;
  /** noBases camp tent: hp = maxHp = 0, never targeted or damaged. */
  tent: 0 | 1;
}
export interface GwWall { hp: number; maxHp: number; x: number; age: number }
/** t = rules.turrets index. */
export interface GwTurret { t: number; nextShot: number; age: number }
/** doneAt = 0 while waiting behind the head. */
export interface GwQueueItem { t: number; doneAt: number; paid: number }

export interface GwTeamStats {
  /** Ore from delivered sacks + claim pay-outs only. */
  oreMined: number;
  oreSpent: number;
  kills: number;
  losses: number;
  unitsTrained: number;
  dmgToBase: number;
  baseHpMin: number;
  /** [a-1] = tick age a was reached: 0 for ages up to the start age, -1 until reached. */
  evolvedAt: number[];
  /** Ticks in which the side held >= 1 centre claim. */
  claimTicks: number;
  /** Full spec cost of every enemy unit that died. */
  valueKilled: number;
}

export type GwAge = 1 | 2 | 3 | 4 | 5;

export interface GwTeamMods {
  /** Mining permille. */
  minePm: number;
  /** Free starting Diggers from difficulty. */
  extraDiggers: number;
  /** Max-HP permille applied at spawn to every unit the side gets, never to the boss. */
  unitHpPm: number;
  /** Outgoing damage permille on every hit the side deals (not specials or burn). */
  dmgPm: number;
  /** Base max-HP permille. */
  baseHpPm: number;
  /** Ore per 30-tick pulse. */
  trickle: number;
  /** Tree discounts (1000 = none). */
  costPm: number;
  /** Extra ore at setup. */
  startOre: number;
  /** spec effect.stat -> round(10 × Σ level × perLevel) (integer tenths), built in spec order. */
  tree: Record<string, number>;
}

/** Revision 2 classes: a hard-coded copy of spec.classes[].id in train-button order (shared cannot import the spec). */
export const GW_CLASS_IDS = ['digger', 'blade', 'shooter', 'guard', 'breaker', 'wild'] as const;
export type GwClassId = (typeof GW_CLASS_IDS)[number];
export type GwTrack = 'weapon' | 'vitality' | 'swiftness' | 'mending';
export const GW_TRACKS: readonly GwTrack[] = ['weapon', 'vitality', 'swiftness', 'mending'];
/** Key order fixed (hash). weapon 1..5 (always 1 for the Digger); the others 0..5. */
export interface GwGrades { weapon: number; vitality: number; swiftness: number; mending: number }
export const GW_BASE_GRADES: GwGrades = { weapon: 1, vitality: 0, swiftness: 0, mending: 0 };
/** Highest grade or level of any track. */
export const GW_GRADE_MAX = 5;

export function isGwClassId(v: unknown): v is GwClassId {
  return typeof v === 'string' && (GW_CLASS_IDS as readonly string[]).includes(v);
}

/** All six classes at the base grades, in GW_CLASS_IDS order (fresh objects). */
export function gwBaseGradeTable(): Record<GwClassId, GwGrades> {
  const out = {} as Record<GwClassId, GwGrades>;
  for (const id of GW_CLASS_IDS) out[id] = { ...GW_BASE_GRADES };
  return out;
}

export interface GwTeam {
  side: GwSide;
  age: GwAge;
  /** Campaign cap, skirmish maxAge. */
  maxAge: GwAge;
  ore: number;
  worms: number;
  /** Derived (age + research + tree), stored for the HUD. */
  popCap: number;
  stance: GwStance;
  base: GwBase;
  wall: GwWall | null;
  /** Length 3. */
  turrets: (GwTurret | null)[];
  /** Turret slots opened (1..3). */
  slots: number;
  /** One FIFO, max rules.queueMax. */
  queue: GwQueueItem[];
  /** Research id -> level bought. */
  research: Record<string, number>;
  /** The single research desk; target level = research[id] + 1. */
  researchJob: { id: string; doneAt: number; paid: number } | null;
  /** `${unitId}:dmg` | `${unitId}:hp` -> level 0..3. */
  kit: Record<string, number>;
  /** The single kit bench, parallel to the desk. */
  kitJob: { unit: string; track: 'dmg' | 'hp'; doneAt: number; paid: number } | null;
  /** Hold flag in cx, always resolved. */
  flagX: number;
  special: { readyAt: number; castCount: number };
  /** Research ids the level forbids, plus 'digger' under noMining. */
  locked: string[];
  /** Level unlocked.*; all true outside the campaign. */
  gates: { kits: boolean; turrets: boolean; walls: boolean; specials: boolean };
  /** OR-ed into the flags of every unit the side spawns. */
  spawnFlags: number;
  /** Player ids that command this side ([] = AI only). */
  controllers: string[];
  /** Present when an AI drives (or co-drives) the side. */
  ai: GwAiMemory | null;
  /** The AI's own RNG state. */
  aiRng: number;
  /** Fixed at setup (3.6). */
  mods: GwTeamMods;
  /** Class grades for the whole battle (keys in GW_CLASS_IDS order), set by setup. */
  grades: Record<GwClassId, GwGrades>;
  /** Unit ids the side may field; never shrinks. */
  roster: string[];
  /** Tick of the next base trickle. */
  nextTrickle: number;
}

export interface GwScriptState {
  levelId: string;
  /** Next index into level.enemy.waves. */
  waveIdx: number;
  lastStandDone: 0 | 1;
  /** Queued free spawns. */
  pending: { at: number; t: number; side: GwSide; flags: number; stance: GwStance }[];
  /** Escort: unit id of the cart, 0 = none. */
  cartId: number;
  /** Warden unit id, arrival tick, death tick (0 = not yet). */
  bossId: number;
  bossAt: number;
  bossDeadAt: number;
  /** meteorShower: tick of the next warning draw, 0 = none. */
  nextMeteor: number;
  /** Bonus ledger. */
  vars: Record<string, number>;
  /** Sticky: a fail-on-event bonus has broken. */
  bonusFailed: boolean;
  promptsDone: string[];
  /** Last 3 story messages. */
  msgs: { at: number; key: string }[];
}

export type GwAiProfileId = 'tutorial' | 'rusher' | 'turtle' | 'boomer' | 'balanced' | 'scripted';
export type GwAiSimProfileId = Exclude<GwAiProfileId, 'scripted'>;
export type GwDifficulty = 'easy' | 'normal' | 'hard' | 'brutal';
export const GW_DIFFICULTIES: readonly GwDifficulty[] = ['easy', 'normal', 'hard', 'brutal'];

export interface GwAiMemory {
  /** Never 'scripted': a scripted level runs enemy.baseProfile. */
  profile: GwAiSimProfileId;
  nextThink: number;
  phase: 'open' | 'build' | 'push' | 'defend' | 'evolve';
  wantDiggers: number;
  saveFor: '' | 'evolve' | 'turret' | 'wall' | 'research';
  lastStanceAt: number;
  /** Retreat: hold/burrow until tick. */
  holdUntil: number;
  /** Own army value sampled once per second over the retreat window. */
  armyRing: number[];
  burrowed: 0 | 1;
  /** Set by lastStand, the boss rule and the stall push. */
  attackForever: 0 | 1;
}

export type GwEndReason = '' | 'base' | 'surrender' | 'script' | 'timeout';

export interface GwState {
  v: 1;
  tick: number;
  seed: number;
  /** Combat RNG state. */
  rng: number;
  /** Next id for units, projectiles, effects (shared counter). */
  nextId: number;
  /** Skirmish/coop/pvp: spec map id; campaign: the level id. */
  map: string;
  /** Lane length, cx. */
  len: number;
  teams: [GwTeam, GwTeam];
  /** Sorted by id. */
  units: GwUnit[];
  projectiles: GwProjectile[];
  seams: GwSeam[];
  effects: GwEffect[];
  /** Sparse timed buffs: k 1 = shielded, k 2 = pierce; sorted by (u, k). */
  buffs: { u: number; k: 1 | 2; to: number }[];
  /** Player id (or 'ai:<side>') -> unit id. */
  champions: Record<string, number>;
  /** Campaign only. */
  script: GwScriptState | null;
  stats: [GwTeamStats, GwTeamStats];
  winner: GwSide | -1;
  /** 0 while running. */
  endTick: number;
  endReason: GwEndReason;
}
